import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Settings as SettingsIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Learn', href: '/learn' },
  { label: 'Defend', href: '/defend' },
  { label: 'Launch', href: '/launch' },
  { label: 'Reach', href: '/reach' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Contact', href: '/contact' },
];

const ctaButtonStyle = {
  background: 'var(--a)',
  color: '#fff',
  boxShadow: '0 0 24px rgba(139,92,246,0.25)',
};

function AccountDropdown({ onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button
        data-testid="nav-account-button"
        onClick={() => setOpen((o) => !o)}
        className="btn-shimmer font-body text-sm font-medium px-5 py-2 rounded-[3px] transition-all duration-200"
        style={ctaButtonStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--a-bright)';
          e.currentTarget.style.boxShadow = '0 0 36px rgba(139,92,246,0.45)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--a)';
          e.currentTarget.style.boxShadow = '0 0 24px rgba(139,92,246,0.25)';
        }}
      >
        <span className="inline-flex items-center" style={{ gap: '0.35rem' }}>
          My Account <ChevronDown size={14} />
        </span>
      </Button>

      {open && (
        <div
          data-testid="nav-account-dropdown"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 0.5rem)',
            minWidth: 160,
            background: 'var(--l1, rgba(20,18,30,0.95))',
            border: '1px solid var(--b1)',
            borderRadius: 10,
            padding: '0.4rem 0',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
            zIndex: 60,
          }}
        >
          <DropdownLink testId="nav-account-dashboard" href="/dashboard" onClose={() => setOpen(false)}>
            Dashboard
          </DropdownLink>
          <DropdownLink testId="nav-account-billing" href="/dashboard/billing" onClose={() => setOpen(false)}>
            Billing
          </DropdownLink>
          <DropdownLink
            testId="nav-account-settings"
            href="/dashboard/settings"
            onClose={() => setOpen(false)}
          >
            <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
              <SettingsIcon size={13} /> Settings
            </span>
          </DropdownLink>
          <button
            type="button"
            data-testid="nav-account-signout"
            onClick={() => { setOpen(false); onLogout(); }}
            style={{
              display: 'block', width: '100%',
              padding: '0.55rem 0.9rem',
              background: 'none', border: 'none',
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem',
              color: 'var(--white)', textAlign: 'left', cursor: 'pointer',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function DropdownLink({ href, testId, onClose, children }) {
  return (
    <a
      href={href}
      data-testid={testId}
      onClick={onClose}
      style={{
        display: 'block',
        padding: '0.55rem 0.9rem',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '0.85rem',
        color: 'var(--white)',
        textDecoration: 'none',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      {children}
    </a>
  );
}

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, tokens, logout } = useAuth();
  const isLoggedIn = !!(user && tokens);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 border-b"
      style={{
        background: 'rgba(5,5,10,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderColor: 'var(--b1)',
      }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="/"
            data-testid="navbar-logo"
            className="flex items-center"
            style={{ color: 'var(--white)' }}
            aria-label="Acodia — Home"
          >
            <img
              src="/images/logo/logo-nav-64.png"
              alt="Acodia"
              className="w-auto h-[36px] sm:h-[44px]"
              style={{ display: 'block', width: 'auto' }}
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="relative px-3 py-2 text-sm font-body transition-colors duration-200 group"
                style={{ color: 'var(--ts)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--white)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--ts)'}
              >
                {link.label}
                <span
                  className="absolute bottom-0 left-3 right-3 h-[1px] origin-left scale-x-0 transition-transform duration-200"
                  style={{ background: 'var(--a)', transform: 'scaleX(0)' }}
                  ref={(el) => {
                    if (el) {
                      const parent = el.parentElement;
                      parent.addEventListener('mouseenter', () => el.style.transform = 'scaleX(1)');
                      parent.addEventListener('mouseleave', () => el.style.transform = 'scaleX(0)');
                    }
                  }}
                />
              </a>
            ))}
          </div>

          {/* CTA — Sign In + Get Started (logged out) or My Account dropdown (logged in) */}
          <div className="hidden md:flex items-center gap-2">
            {isLoggedIn ? (
              <AccountDropdown onLogout={handleLogout} />
            ) : (
              <>
                <a href="/auth">
                  <Button
                    data-testid="nav-signin-button"
                    className="font-body text-sm font-medium px-4 py-2 rounded-[3px] transition-all duration-200"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--b1)',
                      color: 'var(--ts)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'var(--white)';
                      e.currentTarget.style.borderColor = 'var(--b2, rgba(255,255,255,0.18))';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--ts)';
                      e.currentTarget.style.borderColor = 'var(--b1)';
                    }}
                  >
                    Sign In
                  </Button>
                </a>
                <a href="/auth">
                  <Button
                    data-testid="nav-cta-button"
                    className="btn-shimmer font-body text-sm font-medium px-5 py-2 rounded-[3px] transition-all duration-200"
                    style={ctaButtonStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--a-bright)';
                      e.currentTarget.style.boxShadow = '0 0 36px rgba(139,92,246,0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--a)';
                      e.currentTarget.style.boxShadow = '0 0 24px rgba(139,92,246,0.25)';
                    }}
                  >
                    Get Started
                  </Button>
                </a>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            data-testid="navbar-mobile-toggle"
            className="md:hidden p-2"
            style={{ color: 'var(--white)' }}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4 border-t" style={{ borderColor: 'var(--b1)' }}>
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="block px-3 py-3 text-sm font-body transition-colors duration-200"
                style={{ color: 'var(--ts)' }}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            {isLoggedIn ? (
              <>
                <a
                  href="/dashboard"
                  data-testid="nav-mobile-dashboard"
                  className="block px-3 py-3 text-sm font-body"
                  style={{ color: 'var(--white)' }}
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </a>
                <a
                  href="/dashboard#billing"
                  data-testid="nav-mobile-billing"
                  className="block px-3 py-3 text-sm font-body"
                  style={{ color: 'var(--white)' }}
                  onClick={() => setIsOpen(false)}
                >
                  Billing
                </a>
                <button
                  type="button"
                  data-testid="nav-mobile-signout"
                  onClick={() => { setIsOpen(false); handleLogout(); }}
                  className="block w-full text-left px-3 py-3 text-sm font-body"
                  style={{ color: 'var(--white)', background: 'none', border: 'none' }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <a href="/auth" className="block mt-2" data-testid="nav-mobile-signin">
                  <Button
                    className="w-full font-body text-sm rounded-[3px]"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--b1)',
                      color: 'var(--white)',
                    }}
                  >
                    Sign In
                  </Button>
                </a>
                <a href="/auth" className="block mt-2">
                  <Button
                    className="w-full btn-shimmer font-body text-sm rounded-[3px]"
                    style={{ background: 'var(--a)', color: '#fff' }}
                  >
                    Get Started
                  </Button>
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
